import doubleArrowIcon from "@assets/icons/double_arrow.svg";
import editSquareIcon from "@assets/icons/edit_square.svg";
import { CONVERSATION_RETENTION_DAYS } from "@config";
import ROUTES from "@constants/routing";
import { InputContext } from "@context/Input/InputContext";
import { UserContext } from "@context/User/UserContext";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Drawer,
  IconButton,
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  useTheme
} from "@mui/material";
import {
  conversationService,
  Conversation,
  AuthenticationError
} from "@services/conversations";
import React, { useState, useCallback, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";

export interface HamburgerMenuProps {
  color?: string;
}

// Conversation interface is now imported from @services/conversations

// Utility function moved outside component to avoid recreation on each render
const formatTime = (date: Date): string => {
  const day = date.getDate();
  const monthNames = [
    "Jan.",
    "Feb.",
    "Mar.",
    "Apr.",
    "May",
    "Jun.",
    "Jul.",
    "Aug.",
    "Sep.",
    "Oct.",
    "Nov.",
    "Dec."
  ];
  const month = monthNames[date.getMonth()];
  return `${day} ${month}`;
};

interface MenuContentProps {
  conversations: Conversation[];
  onClose: () => void;
  onNewChat: () => void;
  onSelectConversation: (threadId: string) => void;
  isLoading?: boolean;
  isSubscriber?: boolean;
}

const CONVERSATION_RETENTION_NOTICE_DISMISSED_KEY =
  "conversation_retention_notice_dismissed";

const MenuContent: React.FC<MenuContentProps> = ({
  conversations,
  onClose,
  onNewChat,
  onSelectConversation,
  isLoading = false,
  isSubscriber = false
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [showRetentionNotice, setShowRetentionNotice] = useState(() => {
    const dismissed = window.localStorage.getItem(
      CONVERSATION_RETENTION_NOTICE_DISMISSED_KEY
    );
    return dismissed !== "1";
  });

  const handleDismissRetentionNotice = () => {
    window.localStorage.setItem(
      CONVERSATION_RETENTION_NOTICE_DISMISSED_KEY,
      "1"
    );
    setShowRetentionNotice(false);
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        bgcolor: theme.palette.background.paper,
        position: "relative",
        paddingTop: {
          xs: "max(env(safe-area-inset-top, 0px), 20px)",
          sm: 0
        }
      }}
    >
      {/* Header */}
      <Box>
        {/* Top bar with close button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            p: 2,
            pb: 1
          }}
        >
          <IconButton
            onClick={onClose}
            size="small"
            sx={{
              color: theme.palette.customColors.textPrimary,
              "&:hover": {
                backgroundColor: theme.palette.customColors.hoverBackground
              }
            }}
          >
            <img
              src={doubleArrowIcon}
              alt="Close menu"
              style={{ width: 20, height: 20 }}
            />
          </IconButton>
        </Box>

        {/* Divider line */}
        <Box
          sx={{
            borderBottom: `1px solid ${theme.palette.customColors.borderLight}`,
            mb: 1
          }}
        />

        {/* New conversation button */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            px: 2,
            py: 1.5
          }}
          onClick={onNewChat}
        >
          <img
            src={editSquareIcon}
            alt="Edit"
            style={{ width: 20, height: 20 }}
          />
          <Typography
            sx={{
              fontFamily: theme.typography.fontFamily,
              fontSize: "18px",
              fontWeight: 325,
              lineHeight: "18px",
              letterSpacing: "0%",
              color: theme.palette.customColors.textPrimary
            }}
          >
            {t("navbar.new_chat", "Nueva conversación")}
          </Typography>
        </Box>

        {/* Divider line between New conversation and Conversations */}
        {isSubscriber && (
          <Box
            sx={{
              borderBottom: `1px solid ${theme.palette.customColors.borderLight}`,
              mt: 1
            }}
          />
        )}
      </Box>

      {/* Conversations Section - Only for subscribers */}
      {isSubscriber && (
        <>
          {/* Conversations Section Title */}
          <Box>
            <Box
              sx={{
                px: 2,
                py: 1.5
              }}
            >
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 325,
                  fontSize: "12px",
                  lineHeight: "12px",
                  letterSpacing: "0%",
                  color: theme.palette.customColors.textQuaternary,
                  textTransform: "uppercase"
                }}
              >
                {t("navbar.conversations", "CONVERSES")}
              </Typography>
            </Box>
            <Box
              sx={{
                borderBottom: `1px solid ${theme.palette.customColors.borderLight}`
              }}
            />
          </Box>

          {/* Retention Notice - Only show if there are conversations */}
          {showRetentionNotice && conversations.length > 0 && (
            <Box
              sx={{
                mx: 2,
                mt: 2,
                mb: 1,
                p: 1.5,
                borderRadius: 2,
                bgcolor: theme.palette.customColors.hoverBackground,
                position: "relative"
              }}
            >
              <IconButton
                onClick={handleDismissRetentionNotice}
                size="small"
                sx={{
                  position: "absolute",
                  top: 4,
                  right: 4,
                  p: 0.5,
                  color: theme.palette.customColors.textPrimary,
                  "&:hover": {
                    backgroundColor: "transparent"
                  }
                }}
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
              <Typography
                sx={{
                  fontFamily: theme.typography.fontFamily,
                  fontWeight: 325,
                  fontSize: "14px",
                  lineHeight: "18px",
                  letterSpacing: "0%",
                  color: theme.palette.customColors.textPrimary,
                  pr: 3
                }}
              >
                {t("conversation_retention_notice", {
                  days: CONVERSATION_RETENTION_DAYS
                })}
              </Typography>
            </Box>
          )}

          {/* Conversations List */}
          <Box sx={{ flex: 1, overflow: "auto", position: "relative" }}>
            {isLoading ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: theme.palette.customColors.textDisabled
                }}
              >
                <Typography variant="body2">
                  {t(
                    "navbar.loading_conversations",
                    "Cargando conversaciones..."
                  )}
                </Typography>
              </Box>
            ) : conversations.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: "center",
                  color: theme.palette.customColors.textDisabled
                }}
              >
                <Typography variant="body2">
                  {t(
                    "navbar.no_conversations",
                    "No hay conversaciones anteriores"
                  )}
                </Typography>
              </Box>
            ) : (
              <List sx={{ py: 0 }}>
                {conversations.map((conv: Conversation) => (
                  <ListItem key={conv.id} disablePadding>
                    <ListItemButton
                      onClick={() => onSelectConversation(conv.thread_id)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        "&:hover": {
                          backgroundColor:
                            theme.palette.customColors.hoverBackground
                        }
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: theme.typography.fontFamily,
                              fontWeight: 325,
                              fontSize: "18px",
                              lineHeight: "18px",
                              letterSpacing: "0%",
                              color: theme.palette.customColors.textPrimary,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {conv.title}
                          </Typography>
                        }
                        secondary={
                          <Typography
                            component="span"
                            variant="caption"
                            sx={{
                              fontFamily: theme.typography.fontFamily,
                              fontWeight: 325,
                              fontSize: "12px",
                              lineHeight: "12px",
                              letterSpacing: "0%",
                              color: theme.palette.customColors.textQuaternary,
                              mt: 0.5,
                              display: "block"
                            }}
                          >
                            {formatTime(conv.timestamp)}
                          </Typography>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

const HamburgerMenu: React.FC<HamburgerMenuProps> = ({ color }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const _location = useLocation();
  const userCtx = useContext(UserContext);
  const inputCtx = useContext(InputContext);
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load conversations when menu opens
  useEffect(() => {
    const loadConversations = async () => {
      if (!open || !userCtx?.user) {
        return;
      }

      setIsLoading(true);
      try {
        const userId = userCtx.user.userId?.toString();
        const convs = await conversationService.getConversations(userId);
        setConversations(convs);
      } catch (error) {
        // Only handle authentication errors explicitly
        // Other errors (endpoint not available, network issues) are handled silently
        if (error instanceof AuthenticationError) {
          // Authentication error will be handled by AuthGuard component
          console.warn(
            "Authentication error loading conversations:",
            error.translationKey
          );
        }
        // Silently set empty array for all other errors (endpoint might not be available)
        setConversations([]);
      } finally {
        setIsLoading(false);
      }
    };

    loadConversations();
  }, [open, userCtx?.user]);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleNewChat = useCallback(() => {
    setOpen(false);
    // Clear any initial message or audio from InputContext
    if (inputCtx) {
      inputCtx.setInitialMessage("");
      inputCtx.setInitialAudio(null);
    }
    // Navigate to welcome page
    navigate(ROUTES.WELCOME, { replace: true });
  }, [navigate, inputCtx]);

  const handleSelectConversation = useCallback(
    (threadId: string) => {
      setOpen(false);
      // Use replace: true to ensure navigation is detected even if already on chatbot route
      navigate(`${ROUTES.CHATBOT}?thread_id=${threadId}`, { replace: true });
    },
    [navigate]
  );

  return (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        sx={{
          p: 0.5,
          color: color || "inherit",
          "&:hover": {
            backgroundColor: theme.palette.customColors.hoverBackground
          }
        }}
        aria-label={t("navbar.menu")}
      >
        <MenuIcon sx={{ fontSize: 24 }} />
      </IconButton>

      <Drawer
        anchor="left"
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "85%", sm: 320 },
            maxWidth: "85%",
            boxShadow: "2px 0 8px rgba(0, 0, 0, 0.1)"
          }
        }}
      >
        <MenuContent
          conversations={conversations}
          onClose={handleClose}
          onNewChat={handleNewChat}
          onSelectConversation={handleSelectConversation}
          isLoading={isLoading}
          isSubscriber={userCtx?.isSubscriber || false}
        />
      </Drawer>
    </>
  );
};

export default HamburgerMenu;
